import type { useDispatch } from "react-redux"
import { Creators } from "model/apiWrapper"

// Shared by the two PAT hooks: `modules/user-profile/hooks/use-pat` (the self-service card)
// and `modules/account-pats/hooks/use-account-pats` (the admin grid). Both talk to the same
// PAT endpoints through the same dispatch-wrapped fetch layer, so they read errors the same
// way. Keeping one copy is what stops the two readers drifting apart.

// A 403 (ForbiddenError, src/new/api/errors.js) is read by status alone here, whatever body
// the fetch layer (src/new/api/utils.ts) attached. It covers several distinct causes, and
// which ones apply depends on the caller:
// the self-service card sees "PATs disabled for the account" or "no write entitlement
// anywhere", and the admin grid also sees "the flag is off" or "no write entitlement for the
// target member". This reader does not tell them apart, so the message names none of them.
export const FORBIDDEN_MESSAGE = "This action isn't allowed for this account."

// Narrowed with `in` rather than cast: after the guard the property access typechecks on its
// own, so nothing here asserts a shape over a boundary value.
export function isForbidden(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false
  if (!("code" in error)) return false
  const { code } = error
  return code === 403
}

type Dispatch = ReturnType<typeof useDispatch>

export function post<T>(dispatch: Dispatch, url: string, payload?: object): Promise<T> {
  return new Promise((resolve, reject) => {
    dispatch(Creators.post(url, payload, resolve, reject))
  })
}

export function del<T>(dispatch: Dispatch, url: string): Promise<T> {
  return new Promise((resolve, reject) => {
    dispatch(Creators.delete(url, undefined, resolve, reject))
  })
}

// Each field is read behind its own `in` check and then narrowed with `typeof`, so no shape is
// asserted over the error. The server can send `errors` or `response`, and a 403 sends
// neither.
export function getPatErrorMessage(error: unknown): string {
  const fallback = "Something went wrong. Please try again."
  if (typeof error !== "object" || error === null) return fallback
  const errors: unknown = "errors" in error ? error.errors : undefined
  const response: unknown = "response" in error ? error.response : undefined
  if (Array.isArray(errors) && errors.every((entry): entry is string => typeof entry === "string")) {
    return errors.join(", ")
  }
  if (typeof errors === "string") return errors
  // A present but empty body is useless, so fall through to the 403 message or the fallback
  // rather than show a blank notification. No live endpoint sends one today.
  if (typeof response === "string" && response) return response
  if (isForbidden(error)) return FORBIDDEN_MESSAGE
  return fallback
}
